import React from 'react';

import Navbar from '../layout/Navbar';
import Footer from '../layout/Footer';

// A reusable component for each section of the terms to keep the main component clean.
const Section = ({ title, children }) => (
    <section className="mb-8">
        <h2 className="text-2xl font-semibold text-black dark:text-white mb-4">{title}</h2>
        <div className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed space-y-2">
            {children}
        </div>
    </section>
);

// Configuration array for the terms sections. This makes the main component much cleaner
// and easier to update in the future.
const termsSections = [
    {
        id: 1,
        title: '1. Introduction',
        content: (
            <p>
                Welcome to Trueway ("we", "our", or "us"), a personalized job discovery engine in beta version. These Terms of Service ("Terms") govern your access to and use of our website, services, and any related applications (collectively, the "Service"). By accessing or using the Service, you agree to be bound by these Terms. If you do not agree with any part of these Terms, you must not use the Service.
            </p>
        ),
    },
    {
        id: 2,
        title: '2. Beta Version Disclaimer',
        content: (
            <>
                <p>Trueway is currently in beta development by a small independent team. As such:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>The Service may contain bugs, errors, or unexpected behavior</li>
                    <li>Features may change or be removed without notice</li>
                    <li>Data loss may occur during this development phase</li>
                    <li>We provide no uptime guarantees or SLAs</li>
                    <li>Some functionality may be incomplete or experimental</li>
                </ul>
                <p>
                    Your use of this beta version is entirely at your own risk. We strongly recommend maintaining backups of any important data and not relying solely on the Service for critical job search activities during this beta period.
                </p>
            </>
        ),
    },
    {
        id: 3,
        title: '3. Use of the Service',
        content: (
            <>
                <p>
                    You must be at least 18 years old to use the Service. You agree to use the Service only for lawful purposes and in accordance with these Terms. You are responsible for all activities that occur under your account.
                </p>
                <p>You may not:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>Use the Service in any way that violates any applicable law or regulation</li>
                    <li>Interfere with or disrupt the Service or servers or networks connected to the Service</li>
                    <li>Attempt to gain unauthorized access to any part of the Service</li>
                    <li>Use the Service to transmit any viruses or harmful code</li>
                </ul>
            </>
        ),
    },
    {
        id: 4,
        title: '4. User Accounts',
        content: (
            <p>
                To use certain features of the Service, you may need to create an account. You agree to provide accurate and complete information during registration and to keep your account information updated. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.
            </p>
        ),
    },
    {
        id: 5,
        title: '5. Intellectual Property',
        content: (
            <p>
                The Service and all content, features, and functionality (including but not limited to software, text, graphics, logos, and AI-generated content) are owned by Trueway or its licensors and are protected by copyright, trademark, and other intellectual property laws. You may not copy, modify, distribute, or create derivative works based on the Service without our prior written consent.
            </p>
        ),
    },
    {
        id: 6,
        title: '6. User Content',
        content: (
            <p>
                You retain ownership of any content you upload to the Service (e.g., resumes, job preferences). By uploading content, you grant Trueway a non-exclusive, royalty-free license to use, store, and process your content to provide and improve the Service. You represent that you have all rights to upload such content and that it does not violate any third-party rights.
            </p>
        ),
    },
    {
        id: 7,
        title: '7. AI-Generated Content',
        content: (
            <p>
                Trueway uses AI to generate job recommendations and other content. While we strive for accuracy, AI-generated content may contain errors or inaccuracies. You should verify all information before relying on it. We disclaim any liability for decisions made based on AI-generated content.
            </p>
        ),
    },
    {
        id: 8,
        title: '8. Privacy',
        content: (
            <p>
                Your privacy is important to us. Please review our Privacy Policy, which explains how we collect, use, and protect your information. By using the Service, you agree to our Privacy Policy.
            </p>
        ),
    },
    {
        id: 9,
        title: '9. Disclaimers and Limitation of Liability',
        content: (
            <p>
                The Service is provided "as is" without warranties of any kind. We do not guarantee that the Service will be error-free or uninterrupted. To the fullest extent permitted by law, Trueway shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the Service. Our total liability shall not exceed the amount you paid us in the past 12 months.
            </p>
        ),
    },
    {
        id: 10,
        title: '10. Termination',
        content: (
            <p>
                We may terminate or suspend your access to the Service at any time, without notice, for any reason, including violation of these Terms. Upon termination, your right to use the Service will immediately cease.
            </p>
        ),
    },
    {
        id: 11,
        title: '11. Changes to Terms',
        content: (
            <p>
                We may update these Terms from time to time. We will notify you of significant changes by posting the new Terms on the Service. Your continued use of the Service after such changes constitutes your acceptance of the updated Terms.
            </p>
        ),
    },
    {
        id: 12,
        title: '12. Governing Law and Business Status',
        content: (
            <p>
                These Terms shall be governed by the laws of Germany. Please note that Trueway is currently operating as an unincorporated project by a small team of developers. We are not yet a registered business entity in Germany or elsewhere. As we grow, we will formalize our business structure and update these Terms accordingly.
            </p>
        ),
    },
    {
        id: 13,
        title: '13. Contact Us',
        content: (
            <>
                <p>
                    For general questions and inquiries about these Terms, please email us at:
                    <a href="mailto:contact@trueway.app?subject=Trueway%20General%20Question" className="text-blue-600 dark:text-blue-400 hover:underline"> contact@trueway.app</a>.
                </p>
                <p>
                    To report bugs, beta issues, or for technical support, please email us at:
                    <a href="mailto:support@trueway.app?subject=Trueway%20Support%20Request" className="text-blue-600 dark:text-blue-400 hover:underline"> support@trueway.app</a>.
                </p>
                <p>
                    We're a small team but aim to respond within 3-5 business days. For urgent matters, please include "[URGENT]" in your subject line.
                </p>
            </>
        ),
    },
];


/**
 * Renders the Terms of Service page.
 * This component structures the page with a Navbar, the main legal content, and a Footer.
 * The content is dynamically generated from the `termsSections` array for maintainability.
 */
const TermsOfService = () => {
    return (
        <div className="bg-white dark:bg-[#141414] text-black dark:text-[#D9D9D9] transition-colors duration-300 min-h-screen flex flex-col">
            <Navbar />

            <main className="flex-grow py-20 px-8">
                <div className="max-w-4xl mx-auto">
                    <h1 className="text-3xl font-bold text-center text-black dark:text-gray-100 mb-8">
                        Terms of Service
                    </h1>
                    <p className="text-sm text-center text-gray-600 dark:text-[#D9D9D9] mb-12">
                        Effective Date: August 11, 2025
                    </p>

                    {termsSections.map(section => (
                        <Section key={section.id} title={section.title}>
                            {section.content}
                        </Section>
                    ))}

                </div>
            </main>

            <Footer />
        </div>
    );
};

export default TermsOfService;
